import type { PnLanguages } from '@/index';
import { awaitTopbar, en, ripple, reduceMotion } from '@/index';
import { Component, Host, Prop, h, Element, EventEmitter, Event, State, Watch } from '@stencil/core';
import {
  alert_info_circle,
  check_circle,
  alert_exclamation_triangle,
  alert_exclamation_circle,
  close,
} from 'pn-design-assets/pn-assets/icons.js';
import { translations } from './translation';

/**
 * This component is able to fill many roles of a classic toast.
 * It has 4 different colors, a temporary state, hide button, icon and illustration support.
 *
 * The `pn-toast` will expand to its parent containers full width.
 * Either limit the parent width or set a CSS rule to your specific width if needed.
 */
@Component({
  tag: 'pn-toast',
  styleUrl: 'pn-toast.scss',
})
export class PnToast {
  toast: HTMLDivElement;
  button: HTMLButtonElement;

  private readonly duration: number = 400;
  private animationDuration: number = this.duration;
  private animation: Animation;

  @Element() hostElement: HTMLElement;

  @State() isClosing = false;
  @State() isExpanding = false;

  @State() hasSlottedContent: boolean = false;

  /**
   * Set a title for the toast. Requires the `text` prop in order to work.
   * @description Can be used together with slotted content.
   */
  @Prop() heading?: string = '';
  /**
   * Set a paragraph of text for the toast.
   * @description Can be used together with slotted content.
   */
  @Prop() text?: string = '';
  /** Give the toast a HTML ID. */
  @Prop() toastId?: string;
  /**
   * Default color is blue, you also have the option of success (green), warning (orange) and error (red).
   * @category Visual
   */
  @Prop() appearance?: '' | 'success' | 'warning' | 'error';
  /**
   * This will apply a shadow and border to the toast.
   * @category Visual
   */
  @Prop() temporary: boolean = false;
  /**
   * Set an SVG icon.
   * @category Visual
   */
  @Prop() icon?: string;
  /**
   * Set an SVG illustration.
   * @category Visual
   */
  @Prop() illustration?: string;
  /**
   * Control the visibility of the toast. If the `closable` prop is active, the toast will set the `hide` prop on its own.
   * @see {@link closable}
   * @category Features
   */
  @Prop({ mutable: true, reflect: true }) hide: boolean = false;
  /**
   * Show close button.
   * @category Features
   */
  @Prop() closable: boolean = false;
  /**
   * Manually set the language, only needed if the `closable` prop is enabled.
   * @see {@link closable}
   * @category Features
   */
  @Prop({ mutable: true }) language?: PnLanguages = null;

  @Watch('hide')
  handleHide() {
    if (reduceMotion()) this.animationDuration = 0;
    else this.animationDuration = this.duration;

    this.hostElement.style.overflow = 'hidden';

    requestAnimationFrame(() => {
      if (this.hide) this.closeToast();
      else this.openToast();
    });
  }

  /** Event fired when the close button is pressed. */
  @Event() close: EventEmitter<boolean>;
  /**
   * This event is fired when the toast is fully hidden after the animation is finished.
   * Triggers from the close button or if the prop `hide` is set to true.
   * @since v7.6.0
   **/
  @Event() hidden: EventEmitter<boolean>;

  async componentWillLoad() {
    if (this.hide) {
      this.hostElement.style.height = '0';
      this.hostElement.style.overflow = 'hidden';
    }

    if (this.language === null) await awaitTopbar(this.hostElement);
    // Any text content at this stage means there is slotted content present.
    this.hasSlottedContent = !!this.hostElement.textContent;
  }

  componentDidLoad() {
    if (!!this.heading && !this.text && !this.hasSlottedContent)
      console.warn('The %s prop is only available if you use the %s prop or slotted content.', 'heading', 'text');
  }

  private getRect(element: HTMLElement): DOMRect {
    return element.getBoundingClientRect();
  }

  private openToast() {
    requestAnimationFrame(() => {
      const { clientHeight } = this.toast;
      const { height } = this.getRect(this.hostElement);

      this.hostElement.style.height = `${height}px`;

      this.isExpanding = true;
      this.animateToast(true, `${height}px`, `${clientHeight}px`);
    });
  }

  private closeToast() {
    const { clientHeight } = this.hostElement;
    this.hostElement.style.height = `0px`;

    this.isClosing = true;
    this.animateToast(false, `${clientHeight}px`, `0px`);
  }

  private animateToast(open: boolean, startHeight: string, endHeight: string) {
    this.cancelAnimations();
    this.animation = this.hostElement.animate(
      {
        height: [startHeight, endHeight],
      },
      {
        duration: this.animationDuration,
        easing: 'cubic-bezier(0.6, 0, 0.2, 1)',
      },
    );
    this.animation.onfinish = () => this.animationFinish();
    this.animation.oncancel = () => (open ? (this.isExpanding = false) : (this.isClosing = false));
  }

  private animationFinish() {
    this.cancelAnimations();
    if (this.hide) this.hidden.emit(this.hide);
    else {
      this.hostElement.style.height = '';
      this.hostElement.style.overflow = '';
    }
    this.isClosing = false;
    this.isExpanding = false;
  }

  private cancelAnimations() {
    if (this.animation) this.animation.cancel();
  }

  private handleClick(event: PointerEvent) {
    this.hide = true;
    this.close.emit(this.hide);
    ripple(event, this.toast);
  }

  private getIcon(): string {
    if (this.illustration) return '';
    if (this.icon) return this.icon;
    if (this.appearance === 'success') return check_circle;
    if (this.appearance === 'warning') return alert_exclamation_triangle;
    if (this.appearance === 'error') return alert_exclamation_circle;
    return alert_info_circle;
  }

  private getRole(): string {
    return this.appearance === 'error' ? 'alert' : 'status';
  }

  private showIcon(): boolean {
    return !this.illustration;
  }

  private showIllustration(): boolean {
    return !this.showIcon();
  }

  private showTitle(): boolean {
    return (this.hasSlottedContent || !!this.text) && !!this.heading;
  }

  private translate(prop: string): string {
    return translations?.[prop]?.[this.language || en] || prop;
  }

  private isMoving(): boolean {
    return this.isClosing || this.isExpanding;
  }

  private hideToast(): boolean {
    return this.hide && !this.isMoving();
  }

  render() {
    return (
      <Host>
        <div
          id={this.toastId}
          class="pn-toast"
          role={this.getRole()}
          data-appearance={this.appearance}
          data-temporary={this.temporary}
          data-hide={this.hideToast()}
          ref={el => (this.toast = el)}
        >
          {this.showIcon() && <pn-icon class="pn-toast-icon" icon={this.getIcon()} />}

          <div class="pn-toast-content">
            {this.showTitle() && <h4 class="pn-toast-heading">{this.heading}</h4>}
            {this.text && <p class="pn-toast-text">{this.text}</p>}
            <slot />
          </div>

          {this.showIllustration() && <pn-illustration illustration={this.illustration} width="5.5em" height="5.5em" />}

          {this.closable && (
            <button
              class="pn-toast-button"
              type="button"
              aria-label={this.translate('HIDE')}
              onClick={(event: PointerEvent) => this.handleClick(event)}
              ref={el => (this.button = el)}
            >
              <pn-icon icon={close} />
            </button>
          )}
        </div>
      </Host>
    );
  }
}
